<template>
  <ak-card-header class="flex flex--center">
    <h4 class="color-grey">Student Recruitment</h4>
    <div class="ml-auto">
      <div>
        <ak-select
          v-model="selectedPipelineName"
          inline-edit
          :options="pipelineNames"
          placeholder="Select a pipeline"
          class="min-width-150 max-width-200"
          max-height="200"
          deselect-label=""
          select-label=""
          selected-label=""
          :model-value="selectedPipelineName"
          @select="pipelineSelected"
        />
      </div>
    </div>
    <div v-if="isOwnAccount()">
      <ak-tooltip :placement="'left'" :has-arrow="false">
        <template #trigger>
          <a
            class="icon-action icon-action--inline icon-action--offset icon-cog color-grey-light"
            @click="changeWidget()"
          ></a>
        </template>
        Change Widget
      </ak-tooltip>
    </div>
  </ak-card-header>
  <ak-card-body
    id="studentRecruitmentContainer"
    ref="containerRef"
    :size="'flush'"
    class="h-full m-tiny"
  >
    <!-- Loading section -->
    <ak-loading-spinner v-if="loading" :size="'default'" class="m-auto" />
    <!-- No data message -->
    <div v-if="noData && !loading" class="m-auto">
      <div class="text-center">
        <i class="icon--large icon-stats-bars color-grey" />
        <h4 class="color-grey mb-small">No data available</h4>
      </div>
    </div>
    <!-- NoPipelineSelected with same dropdown in header to select pipeline -->
    <div v-if="noPipelineSelected && !loading" class="m-auto">
      <div class="text-center">
        <i class="icon--gigantic fa-solid fa-chart-mixed color-grey" />
        <!-- Need to do US spelling (visualise, enrollment) -->
        <p>
          Start generating {{ contactsBranding }}
          {{ $t('to visualise your enrolment funnel in') }}
          {{ !isXEquals() ? 'Akero' : 'XEquals' }}
        </p>
        <h4 class="color-grey mt-small">
          Select a pipeline to view your data:
        </h4>
        <ak-select
          v-model="selectedPipelineName"
          inline-edit="true"
          :options="pipelineNames"
          placeholder="Select a pipeline"
          class="max-width-250 min-width-180 mx-auto"
          deselect-label=""
          select-label=""
          selected-label=""
          :model-value="selectedPipelineName"
          @select="pipelineSelected"
        />
      </div>
    </div>
    <!-- Section for Highchart -->
    <div
      v-if="!loading && !noData && !noPipelineSelected"
      :style="{
        maxWidth: containerWidth + 'px',
        maxHeight: containerHeight + 'px',
      }"
      class="flex flex--center h-full max-one-whole overflow-x overflow-y-hidden"
    >
      <!-- <highcharts class="h-full" :options="chartOptions"></highcharts> -->
      <highcharts
        class="h-full highcharts-container-ctm"
        :options="chartOptions"
      ></highcharts>
    </div>
  </ak-card-body>
</template>

<script>
import {
  defineComponent,
  ref,
  computed,
  onMounted,
  onUnmounted,
  watch,
} from 'vue'
import { Chart } from 'highcharts-vue'
import Highcharts from 'highcharts'
import stockInit from 'highcharts/modules/stock'
import { request } from '@akerolabs/akero-vue/src/utils/request'
import CardHeader from '@akerolabs/akero-vue/src/components/cards/CardHeader.vue'
import CardBody from '@akerolabs/akero-vue/src/components/cards/CardBody.vue'
import { accountObj, agencyObj } from '@akerolabs/akero-vue/src/store'
import { ToastProgrammatic } from '@akerolabs/akero-vue/src'
import { isOwnAccount } from '@akerolabs/akero-vue/src/services/users'
import { entityBranding } from '@akerolabs/akero-vue/src/services/entityBranding.js'
import { isXEquals } from '@akerolabs/akero-vue/src/services/agency'

stockInit(Highcharts)

export default defineComponent({
  name: 'AkWidgetStudentRecruitment',
  components: {
    [CardHeader.name]: CardHeader,
    [CardBody.name]: CardBody,
    highcharts: Chart,
  },
  inheritAttrs: false,
  props: {
    /**
     * @type {Function}
     * @description Update the widget state
     */
    changeWidget: {
      type: Function,
      default: () => ({}),
    },

    /**
     * @type {Function}
     * @description Update the widget state
     */
    setAccountState: {
      type: Function,
      default: () => ({}),
    },

    /**
     * @type {Function}
     * @description Get the widget state
     */
    getAccountState: {
      type: Function,
      default: () => ({}),
    },

    /**
     * @type {String}
     * @description Region/container of the widget (eg. dashRight, dashLeft)
     */
    region: {
      type: String,
      default: '',
    },
  },

  setup(props) {
    const stages = ref({})
    const pipelineStages = ref([])
    const loading = ref(true)
    const noData = ref()
    const initWidget = ref(true)
    const noPipelineSelected = ref()
    const contacts = ref([])
    const pipelines = ref([])
    const selectedPipelineId = ref(null)
    const selectedPipeline = ref({})
    const graphData = ref([])
    const plotBands = ref([])
    const containerWidth = ref(null)
    const containerHeight = ref(null)
    const containerRef = ref(null)

    let firstStage
    const startDate = '2012-01-01T00:00:00.000Z'
    const endDate = moment().toISOString()

    // Graph config
    const chartOptions = ref({
      chart: {
        type: 'area',
        marginTop: 10,
        spacing: [10, 10, 15, 10],
        reflow: true,
        // backgroundColor: '#000000',
        events: {
          /**
           * Chart load event
           */
          load: function () {
            let chart = this
            let maxChartHeight
            let minChartWidth
            /**
             * Get width of the label
             * in the xAxis plotBandss
             * @param {*} text
             * @param {*} style
             * @returns
             */
            function getTextWidth(text, style) {
              var div = document.createElement('div')
              div.style.position = 'absolute'
              div.style.left = '-9999px'
              div.style.whiteSpace = style['white-space']
              div.style.padding = style.padding
              div.innerHTML = text
              document.body.appendChild(div)
              var width = div.clientWidth
              document.body.removeChild(div)
              return width
            }

            /**
             * Find max width of plotBand label
             * @param {*} plotBands
             * @returns
             */
            function getMaxLabelWidth(plotBands) {
              var maxWidth = 0
              plotBands.forEach((band) => {
                const label = band.label
                const width = getTextWidth(label.text, label.style)
                maxWidth = Math.max(maxWidth, width)
              })
              return maxWidth
            }

            if (plotBands.value.length > 0) {
              const maxLabelWidth = getMaxLabelWidth(plotBands.value)
              const padding = 10 // Add some extra padding to avoid overlapping
              minChartWidth = (maxLabelWidth + padding) * plotBands.value.length
              if (minChartWidth < containerWidth.value) {
                minChartWidth = containerWidth.value
              }
              if (!containerWidth.value) {
                containerWidth.value = 400
              }

              if (minChartWidth > containerWidth.value) {
                /**
                 * Update the chart's width and minWidth
                 */
                chart.update({
                  responsive: {
                    conditions: {
                      minWidth: minChartWidth,
                      minHeight: containerHeight.value,
                    },
                  },
                })
                maxChartHeight = containerHeight.value - 10
              }
            } else {
              maxChartHeight = containerHeight.value
            }
            chart.setSize(minChartWidth, maxChartHeight)
          },
        },
      },
      title: {
        text: '',
      },
      yAxis: {
        title: {
          text: 'Number of Contacts',
        },
        plotLines: [
          {
            value: 0,
            width: 1,
          },
        ],
        labels: {
          enabled: false,
        },
        gridLineWidth: 0,
      },
      xAxis: {
        tickLength: 0,
        labels: {
          enabled: false,
        },
      },
      series: [
        {
          color: '#9941f14d',
          pointPadding: 0.2,
          borderWidth: 0,
          data: graphData.value.graphData,
        },
      ],
      plotOptions: {
        color: '#9941f1',
        pointPadding: 0.2,
        borderWidth: 0,
        series: {
          connectNulls: false,
        },
        area: {
          market: {
            enabled: false,
            symbol: 'circle',
            radius: 2,
            states: {
              hover: {
                enabled: true,
              },
            },
          },
          dataLabels: {
            enabled: true,
            useHTML: true,
            formatter: function () {
              var format =
                '<div class="highcharts-data-label-custom mb-tiny">' +
                this.y +
                '</div>'
              return format
            },
          },
        },
      },
      legend: {
        enabled: false,
      },
      tooltip: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      loading: {
        style: {
          opacity: 1,
        },
      },
      navigation: {
        buttonOptions: {
          enabled: false,
        },
      },
    })

    /**
     * @description Pipeline names used for dropdown
     */
    const pipelineNames = computed(() =>
      pipelines.value.map((pipeline) => pipeline.name),
    )

    /**
     * @description Account define contact branding
     */
    const contactsBranding = computed(() => {
      return entityBranding('contacts.plural', 'Contacts')
    })

    /**
     * @description Getter and setter for selectedPipeline
     */
    const selectedPipelineName = computed({
      get: () => (selectedPipeline.value ? selectedPipeline.value.name : ''),
      set: (name) => {
        selectedPipeline.value = pipelines.value.find(
          (pipeline) => pipeline.name === name,
        )
      },
    })

    /**
     * @description Watch for changes in selectedPipeline
     */
    watch(selectedPipeline, (newVal) => {
      // if newVal is empty, set noPipelineSelected to true
      if (!newVal) {
        noPipelineSelected.value = true
        loading.value = false
        noData.value = false
        return
      }
      pipelineSelected(newVal)
    })

    /**
     * @description Increments stages in pipeline
     */
    const countStatuses = (obj) => {
      for (let [key, value] of Object.entries(obj)) {
        if (key !== 'total') {
          stages.value[key] = value
        }
      }
    }

    /**
     * @description Queries contacts. Fills contacts array
     */
    const getContacts = async () => {
      let contactsData
      const displayError = (ex) => {
        if (ex) console.log(ex)
        ToastProgrammatic.open({
          message: `Error failed to load requests`,
          type: 'danger',
        })
      }
      try {
        const { data } = await request.get(
          '/contacts/stats',
          contactsRequest.value,
        )
        contactsData = data.data
      } catch (ex) {
        displayError(ex)
      }
      contacts.value = contactsData
      countStatuses(contactsData) // Increment stages
    }

    /**
     * @description Contacts api request
     * @type {Object}
     */
    const contactsRequest = computed(() => ({
      params: {
        account: accountObj()._id,
        agency: agencyObj()._id,
        pipeline: selectedPipeline.value._id,
        createdOn: JSON.stringify({
          $gte: startDate,
          $lte: endDate,
        }),
        firstCol: firstStage,
      },
    }))

    /**
     * @description Queries pipelines. Fills pipelines array
     */
    const getPipelines = async () => {
      let pipelineData = []
      try {
        const { data } = await request.get('/pipelines', pipelinesRequest.value)
        pipelineData = data.data
      } catch (ex) {
        pipelineData = []
      }
      pipelines.value = pipelineData
    }

    /**
     * @description Pipelines api request
     * @type {Object}
     */
    const pipelinesRequest = computed(() => ({
      params: {
        account: accountObj()._id,
        agency: agencyObj()._id,
        deleted: JSON.stringify({ $ne: true }),
        per_page: 0,
      },
    }))

    /**
     * @description Queries companies
     */
    const getCompanies = async () => {
      let companiesData = []
      try {
        const { data } = await request.get('/companies', companiesRequest.value)
        companiesData = data.data
      } catch (ex) {
        companiesData = []
      }
      countStatuses(companiesData) // Increment stages
    }

    /**
     * @description Companies api request
     * @type {Object}
     */
    const companiesRequest = computed(() => ({
      params: {
        account: accountObj()._id,
        agency: agencyObj()._id,
        pipeline: selectedPipeline.value._id,
        createdOn: JSON.stringify({
          $gte: startDate,
          $lte: endDate,
        }),
        firstCol: firstStage,
      },
    }))

    /**
     * @description Queries opportunities
     */
    const getOpportunities = async () => {
      let opportunitiesData = []
      try {
        const { data } = await request.get(
          '/opportunities',
          opportunitiesRequest.value,
        )
        opportunitiesData = data.data
      } catch (ex) {
        opportunitiesData = []
      }
      countStatuses(opportunitiesData) // Increment stages
    }

    /**
     * @description Opportunities api request
     * @type {Object}
     */
    const opportunitiesRequest = computed(() => ({
      params: {
        account: accountObj()._id,
        agency: agencyObj()._id,
        pipeline: selectedPipeline.value._id,
        createdOn: JSON.stringify({
          $gte: startDate,
          $lte: endDate,
        }),
        firstCol: firstStage,
      },
    }))

    /**
     * @description Builds data used in highcharts
     */
    const buildData = async () => {
      // Wait for the stages to populate (using a timeout as a placeholder for the actual wait condition)
      await new Promise((resolve) => setTimeout(resolve, 3000))

      const data = Object.entries(stages.value)
        .filter(([key]) => key !== 'total')
        .map(([key, value]) => ({ y: parseInt(value, 10), name: key }))

      graphData.value = data
      noData.value = data.every((item) => item.y === 0)
      if (data.length > 0) {
        configureChartOptions(graphData)
      }

      loading.value = false
    }

    /**
     * @param {Array} graphData
     * @description Configures chart options. Add plotBands and categories
     * @returns void
     */
    const configureChartOptions = (graphData) => {
      chartOptions.value.series[0].data = graphData.value

      // Initialize xAxis if it doesn't exist
      if (!chartOptions.value.xAxis) {
        chartOptions.value.xAxis = {}
      }

      plotBands.value = []
      for (var i = 0; i < pipelineStages.value.length; i++) {
        plotBands.value.push({
          from: i - 0.5,
          to: i + 0.5,
          borderWidth: 1,
          borderColor: '#f1f1f3',
          color: '#ffffff',
          label: {
            text: pipelineStages.value[i],
            align: 'center',
            style: {
              'white-space': 'nowrap',
            },
          },
        })
      }
      chartOptions.value.xAxis.plotBands = plotBands.value
      chartOptions.value.xAxis.categories = pipelineStages.value
      return
    }

    /**
     * @description Gets the default pipeline for the account
     */
    const getAccountDefaults = async () => {
      if (accountObj().defaultContactPipeline) {
        selectedPipelineId.value = accountObj().defaultContactPipeline
        selectedPipelineName.value = pipelines.value.find(
          (p) => p._id === selectedPipelineId.value,
        ).name
      } else {
        noPipelineSelected.value = true
        loading.value = false
      }
    }

    /**
     * @param {Object} pipeline
     * @description Sets the state of the widget region. Remembers selected on load
     */
    const setState = (pipeline) => {
      props.setAccountState(
        'widgets.data.' + props.region + '._id',
        pipeline._id,
      )
    }

    /**
     * @description Checks for state of widget region. If none, gets account defaults
     */
    const checkForState = async () => {
      let desiredPipeline = props.getAccountState(
        'widgets.data.' + props.region,
      )
      if (
        !desiredPipeline ||
        !desiredPipeline._id ||
        desiredPipeline._id === ''
      ) {
        await getAccountDefaults()
      } else {
        selectedPipeline.value = pipelines.value.find(
          (pipeline) => pipeline._id === desiredPipeline._id,
        )
        if (selectedPipeline.value) {
          pipelineSelected(selectedPipeline.value)
        }
      }
    }

    /**
     * @param {Object} pipeline
     * @description Handles selected pipeline for chart
     */
    const pipelineSelected = async (pipeline) => {
      initWidget.value = false
      noPipelineSelected.value = false
      pipelineStages.value = []
      stages.value = {}
      loading.value = true
      noData.value = false

      setState(pipeline)

      if (pipeline && pipeline.stages) {
        for (var i = 0; i < pipeline.stages.length; i++) {
          pipelineStages.value.push(pipeline.stages[i].name)
          if (i === 0) {
            firstStage = pipeline.stages[0].name
          }
          stages.value[pipeline.stages[i].name] = 0
          if (i === pipeline.stages.length - 1) {
            switch (pipeline.type) {
              case 'opportunities':
                await getOpportunities()
                break
              case 'contacts':
                await getContacts()
                break
              case 'companies':
                await getCompanies()
                break
              case 'any':
                await Promise.all([
                  getOpportunities(),
                  getContacts(),
                  getCompanies(),
                ])
            }
          }
        }
      }
      // Call buildData function on async await
      await buildData()
    }

    /**
     * @description On resize, update the container width and height
     */
    const onResize = () => {
      if (!loading.value && containerRef.value) {
        const container = containerRef.value.$el
        containerWidth.value = container.offsetWidth
        containerHeight.value = container.offsetHeight
      }
    }

    /**
     * @description On mount, get pipelines, check for state, add resize listener
     */
    onMounted(async () => {
      await getPipelines()
      checkForState()

      window.addEventListener('resize', onResize)

      const container = containerRef.value.$el
      containerWidth.value = container.offsetWidth
      containerHeight.value = container.offsetHeight

      onResize()
    })

    /**
     * @description On unmount, remove resize listener
     */
    onUnmounted(() => {
      // Remove resize listener
      window.removeEventListener('resize', onResize)
    })

    return {
      stages,
      loading,
      isOwnAccount,
      selectedPipeline,
      getContacts,
      getPipelines,
      selectedPipelineId,
      pipelineNames,
      selectedPipelineName,
      chartOptions,
      containerWidth,
      containerHeight,
      noData,
      pipelineSelected,
      noPipelineSelected,
      contactsBranding,
      isXEquals,
      containerRef,
    }
  },
})
</script>

<style>
.highcharts-container-ctm {
  overflow-x: auto !important;
  overflow-y: hidden !important;
}
</style>

<i18n>
  {
    "en": {
      "to visualise your enrolment funnel in": "to visualise your enrolment funnel in",
    },
    "en-US": {
      "to visualise your enrolment funnel in": "to visualize your enrollment funnel in",
    },
  }
</i18n>
